import { useCallback, useMemo } from 'react';

import { nanoid } from 'nanoid';

import type {
  ChatHistoryByOrg,
  ChatHistoryItem,
  ChatMessage,
} from '@cast/types';

import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { usePersistentState } from '@cast/utils';

import {
  notifyOpsPilotChatHistoryChanged,
  OPS_PILOT_CHAT_HISTORY_KEY,
} from './opsPilotChatHistoryStorage';

const MAX_HISTORY_ITEMS = 50;
const MAX_TITLE_LENGTH = 50;

const generateTitle = (messages: ChatMessage[]): string => {
  const firstUserMessage = messages.find((m) => m.role === 'user');
  if (!firstUserMessage) return 'New Chat';

  const text = firstUserMessage.text.trim();

  if (text.length <= MAX_TITLE_LENGTH) return text;
  return `${text.slice(0, MAX_TITLE_LENGTH)}...`;
};

export const useChatHistory = () => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id || 'default';

  const [historyByOrg, setHistoryByOrg] = usePersistentState<ChatHistoryByOrg>(
    OPS_PILOT_CHAT_HISTORY_KEY,
    {}
  );

  const history = useMemo(() => {
    const orgHistory = historyByOrg[organizationId] || [];
    return [...orgHistory].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [historyByOrg, organizationId]);

  const saveChat = useCallback(
    (
      messages: ChatMessage[],
      existingChatId: string | undefined,
      contextId: string
    ) => {
      if (messages.length === 0) return null;

      const now = Date.now();
      const chatId = existingChatId || nanoid();

      setHistoryByOrg((prev) => {
        const orgHistory = prev[organizationId] || [];
        const existingIndex = orgHistory.findIndex(
          (item) => item.id === chatId
        );

        const chatItem: ChatHistoryItem = {
          id: chatId,
          title: generateTitle(messages),
          messages,
          createdAt:
            existingIndex >= 0 ? orgHistory[existingIndex].createdAt : now,
          updatedAt: now,
          contextId,
        };

        const updatedHistory =
          existingIndex >= 0
            ? orgHistory.map((item, index) =>
                index === existingIndex ? chatItem : item
              )
            : [chatItem, ...orgHistory];

        const trimmedHistory =
          updatedHistory.length > MAX_HISTORY_ITEMS
            ? updatedHistory.slice(0, MAX_HISTORY_ITEMS)
            : updatedHistory;

        return {
          ...prev,
          [organizationId]: trimmedHistory,
        };
      });
      notifyOpsPilotChatHistoryChanged();

      return chatId;
    },
    [setHistoryByOrg, organizationId]
  );

  const deleteChat = useCallback(
    (chatId: string) => {
      setHistoryByOrg((prev) => {
        const orgHistory = prev[organizationId] || [];
        return {
          ...prev,
          [organizationId]: orgHistory.filter((item) => item.id !== chatId),
        };
      });
      notifyOpsPilotChatHistoryChanged();
    },
    [setHistoryByOrg, organizationId]
  );

  const clearHistory = useCallback(() => {
    setHistoryByOrg((prev) => ({
      ...prev,
      [organizationId]: [],
    }));
    notifyOpsPilotChatHistoryChanged();
  }, [setHistoryByOrg, organizationId]);

  const getChat = useCallback(
    (chatId: string) => history.find((item) => item.id === chatId),
    [history]
  );

  return {
    history,
    saveChat,
    deleteChat,
    clearHistory,
    getChat,
  };
};
